import { Box, Typography, useMediaQuery, IconButton, Drawer, List, ListItemButton, ListItemText } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { Menu as MenuIcon, Close } from '@mui/icons-material'
import { useState } from 'react'
import { COLORS } from '../../constants/colors'
import { TEXT } from '../../constants/text'
import { useTier } from '../../hooks/useTier'
import { TIER_COLORS } from '../../constants/colors'
import WalletButton from './WalletButton'

const goldGlow = keyframes`
  0%, 100% { text-shadow: 0 0 3px ${COLORS.gold.primary}, 0 0 8px ${COLORS.gold.primary}; }
  50% { text-shadow: 0 0 6px ${COLORS.gold.primary}, 0 0 18px ${COLORS.gold.light}; }
`

const HeaderBar = styled(Box)(() => ({
  position: 'sticky',
  top: 0,
  zIndex: 1100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 24px',
  background: 'linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.85) 100%)',
  backdropFilter: 'blur(20px)',
  borderBottom: `1px solid ${COLORS.border.subtle}`,
}))

const LogoArea = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
}))

const NavLinks = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}))

const NavBtn = styled('button')(({ active }) => ({
  background: active ? COLORS.bg.glass : 'transparent',
  border: active ? `1px solid ${COLORS.border.light}` : '1px solid transparent',
  borderRadius: 8,
  padding: '6px 14px',
  color: active ? COLORS.text.primary : COLORS.text.secondary,
  fontFamily: 'inherit',
  fontWeight: 500,
  fontSize: '0.82rem',
  cursor: 'pointer',
  transition: 'all .2s',
  '&:hover': {
    background: COLORS.bg.glass,
    color: COLORS.text.primary,
  },
}))

const TierBadge = styled('span')(({ tier }) => ({
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: TIER_COLORS[tier]?.primary || '#666',
  background: TIER_COLORS[tier]?.bg || COLORS.bg.glass,
  border: `1px solid ${TIER_COLORS[tier]?.border || COLORS.border.light}`,
}))

const NAV_ITEMS = [
  { key: 'feed', label: TEXT.NAV.FEED },
  { key: 'signals', label: TEXT.NAV.SIGNALS },
  { key: 'whales', label: TEXT.NAV.WHALES },
  { key: 'pricing', label: TEXT.NAV.PRICING },
  { key: 'info', label: TEXT.NAV.INFO },
]

export default function Header({ page, onNavigate }) {
  const { tier, config } = useTier()
  const mobile = useMediaQuery('(max-width:900px)')
  const [drawer, setDrawer] = useState(false)

  return (
    <>
      <HeaderBar>
        {/* Logo */}
        <LogoArea onClick={() => onNavigate('feed')}>
          <img
            src="/assets/images/logo.png"
            alt={TEXT.BUSINESS.NAME}
            style={{ height: 32, width: 'auto' }}
            onError={(e) => { e.target.style.display = 'none' }}
          />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                fontSize: '1.05rem',
                color: COLORS.gold.primary,
                animation: `${goldGlow} 3s ease-in-out infinite`,
                lineHeight: 1.2,
              }}
            >
              {TEXT.BUSINESS.NAME}
            </Typography>
            {!mobile && (
              <Typography sx={{ fontSize: '0.6rem', color: COLORS.text.muted, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                {TEXT.BUSINESS.TAGLINE}
              </Typography>
            )}
          </Box>
          <TierBadge tier={tier}>{config.label}</TierBadge>
        </LogoArea>

        {/* Desktop nav */}
        {!mobile && (
          <NavLinks>
            {NAV_ITEMS.map((n) => (
              <NavBtn key={n.key} active={page === n.key ? 1 : 0} onClick={() => onNavigate(n.key)}>
                {n.label}
              </NavBtn>
            ))}
          </NavLinks>
        )}

        {/* Right side */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WalletButton />
          {mobile && (
            <IconButton onClick={() => setDrawer(true)} sx={{ color: COLORS.text.secondary }}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </HeaderBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawer}
        onClose={() => setDrawer(false)}
        PaperProps={{
          sx: {
            background: COLORS.bg.secondary,
            borderLeft: `1px solid ${COLORS.border.subtle}`,
            width: 260,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setDrawer(false)} sx={{ color: COLORS.text.muted }}>
            <Close />
          </IconButton>
        </Box>
        <List>
          {NAV_ITEMS.map((n) => (
            <ListItemButton
              key={n.key}
              selected={page === n.key}
              onClick={() => { onNavigate(n.key); setDrawer(false) }}
              sx={{
                '&.Mui-selected': {
                  background: COLORS.bg.glass,
                  borderRight: `3px solid ${COLORS.gold.primary}`,
                },
              }}
            >
              <ListItemText primary={n.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  )
}
